import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsError, ToastOptionsSuccess } from "@modernary/mui/components/toasts";
import { apiService } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";

import type { Paging, PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { contactMethodItems, personDisplayName } from "./person";
import type { Person, PersonCreate, PersonElectronicForm, PersonFormKit, PersonPermitVersion, PersonQuery } from "./person";
import personService from "./personService";

const URL = "people";
const ENTITY_NAME = "Person";
const axiosParams = { baseURL: import.meta.env.VITE_AQMS_API_BASE_URL };
const api = apiService<Person>(apiHosts.AQMS, axiosParams);

async function applyAqmsDetails(person: Person): Promise<Person> {
  const response = await api.get(`${URL}/${person.id}/aqms-details`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.hideStatusCodes = [404];
    },
  });
  return { ...person, ...(response?.data ?? {}) };
}

async function saveAqmsDetails(person: Person | (PersonCreate & { id: number })) {
  return api.put(`${URL}/${person.id}/aqms-details`, {
    id: person.id,
    hasDustCard: person.hasDustCard,
    dustCardNumber: person.dustCardNumber,
    dustCardExpirationDate: person.dustCardExpirationDate,
  });
}

const getPeople = (params: PersonQuery, config: PagingApiAndNotifierConfig = {}) =>
  api
    .getPaged(URL, {
      params,
      paramsSerializer: serializeQueryParams,
      abort: config.abortMethod,
      successToastCb: (options: ToastOptionsSuccess) => {
        options.hide = true;
        config.successToastCb?.(options);
      },
      errorToastCb: (options: ToastOptionsError) => {
        options.fetched = ENTITY_NAME;
        config.errorToastCb?.(options);
      },
    })
    .then((response) => {
      response.data._items = response.data._items?.map((item) => preparePerson(item));
      return response;
    });

function preparePerson(person: Person): Person {
  personService.addAuthRequestFields(person);
  person.displayName = personDisplayName(person);
  person.contactMethods = contactMethodItems(person.contactMethods);
  return person;
}

const getNewPerson = async () =>
  ({
    data: personService.getInitialData(),
  }) as AxiosResponse<PersonFormKit, any>;

const getPersonById = async (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.get(`${URL}/${id}`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.fetched = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  return { ...response, data: await applyAqmsDetails(response.data) } as AxiosResponse<Person>;
};

const createPerson = async (person: PersonCreate, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.post(URL, person, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.created = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.created = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  await saveAqmsDetails(response.data);
  response.data = {
    ...response.data,
    hasDustCard: person.hasDustCard,
    dustCardNumber: person.dustCardNumber,
    dustCardExpirationDate: person.dustCardExpirationDate,
  };
  return response;
};

const updatePerson = async (person: Person & PersonCreate, config: PagingApiAndNotifierConfig = {}) => {
  const response = await api.put(`${URL}/${person.id}`, person, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.updated = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.updated = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });
  await saveAqmsDetails(person);
  response.data = {
    ...response.data,
    hasDustCard: person.hasDustCard,
    dustCardNumber: person.dustCardNumber,
    dustCardExpirationDate: person.dustCardExpirationDate,
  };
  return response;
};

const savePerson = (person: PersonCreate & { id?: number }, config: PagingApiAndNotifierConfig = {}) => {
  if (person.id) return updatePerson(person as Person & PersonCreate, config);
  return createPerson(person, config);
};

const deletePersonById = (id: string | number, config: PagingApiAndNotifierConfig = {}) =>
  api.delete(`${URL}/${id}`, {
    successToastCb: (options: ToastOptionsSuccess) => {
      options.deleted = ENTITY_NAME;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.deleted = ENTITY_NAME;
      config.errorToastCb?.(options);
    },
  });

const getPersonChildPage = <T>(
  url: string,
  fetched: string,
  params: PersonQuery,
  config: PagingApiAndNotifierConfig,
) =>
  api.getPaged<Paging<T>>(config.pagingUrl ?? url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config.abortMethod,
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.fetched = fetched;
      config.errorToastCb?.(options);
    },
  });

const getPersonElectronicForms = (
  personId: string | number,
  params: PersonQuery,
  config: PagingApiAndNotifierConfig = {},
) =>
  getPersonChildPage<PersonElectronicForm>(`${URL}/${personId}/electronic-forms`, "Electronic Forms", params, config);

const getPersonPermitVersions = (
  personId: string | number,
  params: PersonQuery,
  config: PagingApiAndNotifierConfig = {},
) => getPersonChildPage<PersonPermitVersion>(`${URL}/${personId}/permit-versions`, "Permits", params, config);

export {
  createPerson,
  deletePersonById,
  getNewPerson,
  getPeople,
  getPersonById,
  getPersonElectronicForms,
  getPersonPermitVersions,
  savePerson,
  updatePerson,
};
